import type { Filter } from "@judo/model-api";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { Grid } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { GridFilterModel } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFilterDataGridOperator } from "../utils/filter-operator-utils";
import { filterPanelStyles } from "./filter-panel-styles";
import { IconRenderer } from "./IconRenderer";
import { SingleFilterInput } from "./SingleFilterInput";

/** Default Grid column span for a filter when the model omits `Filter.col`. */
const DEFAULT_FILTER_COL_SIZE = 4;

export interface FixedTableFiltersProps {
	/** Always-shown filters (pre-filtered to alwaysShown === true) */
	filters: Filter[];
	/** Current DataGrid filter model (shared state) */
	filterModel: GridFilterModel;
	/** Callback to update the shared filter model */
	onFilterModelChange: (model: GridFilterModel) => void;
	/** Test ID prefix for the table */
	testId: string;
	/** Whether the filter panel is collapsible. @default true */
	collapsibleFilters?: boolean;
	/** Maximum grid columns for the first (always-visible) filter row. @default 12 */
	collapsibleFiltersMaxCols?: number;
}

/**
 * Renders a row of always-visible filter inputs above the DataGrid.
 * When collapsible, splits filters into a first row and an expandable overflow section.
 * Each filter injects a GridFilterItem into the shared filterModel,
 * so both fixed filters and the DataGrid's built-in filter panel
 * share the same state.
 */
export function FixedTableFilters({
	filters,
	filterModel,
	onFilterModelChange,
	testId,
	collapsibleFilters = true,
	collapsibleFiltersMaxCols = 12,
}: FixedTableFiltersProps) {
	const theme = useTheme();
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

	const { firstRowFilters, additionalFilters } = useMemo(() => {
		if (!collapsibleFilters) {
			return { firstRowFilters: filters, additionalFilters: [] };
		}

		if (!isMdUp) {
			return {
				firstRowFilters: filters.slice(0, 1),
				additionalFilters: filters.slice(1),
			};
		}

		const firstRow: Filter[] = [];
		const additional: Filter[] = [];
		let currentColSum = 0;
		const availableCols = collapsibleFiltersMaxCols;

		for (const filter of filters) {
			const filterCol = Math.round(filter.col ?? DEFAULT_FILTER_COL_SIZE);
			if (firstRow.length === 0 || currentColSum + filterCol <= availableCols) {
				firstRow.push(filter);
				currentColSum += filterCol;
			} else {
				additional.push(filter);
			}
		}

		return { firstRowFilters: firstRow, additionalFilters: additional };
	}, [filters, collapsibleFilters, isMdUp, collapsibleFiltersMaxCols]);

	if (filters.length === 0) {
		return null;
	}

	const filterSize: "small" | undefined = collapsibleFilters ? "small" : undefined;

	// Review refactor R4 — shared row renderer.
	// Previously first-row and overflow-row rendering were two near-identical
	// JSX blocks. Extracting the Grid + FixedFilterItem loop to a closure keeps
	// their styling independent (first row: flex:1 inside the icon+toggle flex-row;
	// overflow row: top margin under Collapse) while eliminating the duplication.
	const renderFilterRow = (rows: Filter[], sx?: Parameters<typeof Grid>[0]["sx"]) => (
		<Grid container spacing={2} alignItems="flex-end" sx={sx}>
			{rows.map((filter) => (
				<FixedFilterItem
					key={filter["xmi:id"] ?? filter.name}
					filter={filter}
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					testId={testId}
					size={filterSize}
				/>
			))}
		</Grid>
	);

	if (!collapsibleFilters) {
		return <div data-testid={`${testId}::fixed-filters`}>{renderFilterRow(filters, { px: 1, py: 1 })}</div>;
	}

	return (
		<Box
			sx={{ ...filterPanelStyles.container(theme), overflow: "hidden", display: "flex", gap: 1.5 }}
			data-testid={`${testId}::fixed-filters`}
		>
			{/* Magnify icon — own column, vertically centered, spans all rows */}
			<Box sx={{ ...filterPanelStyles.iconContainer(theme), alignSelf: "center", flexShrink: 0 }}>
				<SearchIcon sx={filterPanelStyles.icon(theme)} />
			</Box>

			{/* Filters section — first row + collapsible overflow */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					{renderFilterRow(firstRowFilters, { flex: 1, minWidth: 0 })}
					{additionalFilters.length > 0 && (
						<Box sx={{ flexShrink: 0 }}>
							<IconButton
								size="small"
								onClick={() => setExpanded(!expanded)}
								title={
									expanded
										? (t("judo.component.filter.collapse", { defaultValue: "Collapse filters" }) as string)
										: (t("judo.component.filter.expand", { defaultValue: "Expand filters" }) as string)
								}
								sx={filterPanelStyles.toggleButton(theme)}
								data-testid={`${testId}::filter-toggle`}
							>
								<IconRenderer
									icon={expanded ? "menu-up" : "menu-down"}
									sx={filterPanelStyles.toggleButtonIcon(theme)}
									size="small"
								/>
							</IconButton>
						</Box>
					)}
				</Box>
				{additionalFilters.length > 0 && (
					<Collapse in={expanded}>{renderFilterRow(additionalFilters, { mt: 1 })}</Collapse>
				)}
			</Box>
		</Box>
	);
}

/** Wraps a SingleFilterInput with Grid sizing and filterModel read/write. */
function FixedFilterItem({
	filter,
	filterModel,
	onFilterModelChange,
	testId,
	size,
}: {
	filter: Filter;
	filterModel: GridFilterModel;
	onFilterModelChange: (model: GridFilterModel) => void;
	testId: string;
	size?: "small" | "medium";
}) {
	const fieldName = filter.attributeType?.name;
	const stableId = `fixed-${filter["xmi:id"] ?? filter.name}`;
	const operator = getFilterDataGridOperator(filter);

	// Read current value from the shared filterModel
	const currentItem = filterModel.items.find((item) => item.id === stableId);
	const currentValue = currentItem?.value ?? null;

	const handleChange = useCallback(
		(newValue: unknown) => {
			if (!fieldName) return;

			const existingItems = filterModel.items.filter((item) => item.id !== stableId);

			if (newValue === null || newValue === undefined || newValue === "") {
				// Remove filter item when value is cleared
				onFilterModelChange({ ...filterModel, items: existingItems });
			} else {
				// Add/update filter item
				onFilterModelChange({
					...filterModel,
					items: [
						...existingItems,
						{
							id: stableId,
							field: fieldName,
							operator,
							value: newValue,
						},
					],
				});
			}
		},
		[fieldName, filterModel, onFilterModelChange, stableId, operator]
	);

	const colSize = Math.round(filter.col ?? DEFAULT_FILTER_COL_SIZE);

	return (
		<Grid size={{ xs: 12, md: colSize }}>
			<SingleFilterInput filter={filter} value={currentValue} onChange={handleChange} testId={testId} size={size} />
		</Grid>
	);
}
